import Konva from 'konva';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../CanvasEditor.css';
import { useShallow } from 'zustand/react/shallow';

import CanvasControls from './components/CanvasControls';
import CanvasSidebar from './components/CanvasSidebar';
import CanvasStage from './components/CanvasStage';
import TextEditor from './components/TextEditor';

import useAnimation from './hooks/useAnimation';
import useCanvasElements from './hooks/useCanvasElements';
import useCanvasHistory from './hooks/useCanvasHistory';
import { useFileHandling } from './hooks/useFileHandling';
import useKeyboardEventHandler from './hooks/useKeyboardEventHandler';
import useTextEditing from './hooks/useTextEditing';
import useTransformHandlers from './hooks/useTransformHandlers';

import { useCanvasStore } from './store/canvasStore';
import type { AnimationType, EditingStyle, LogoProps, SelectionType, TextItem } from './types';
import { exportCanvasImage } from './utils/canvas';

interface CanvasEditorProps {
  onSave?: () => void;
  hideCreateThumbnail?: boolean;
}

interface SidebarProps {
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  logoInputRef: React.RefObject<HTMLInputElement | null>;
  handleAddText: () => void;
  handleUndo: () => void;
  handleRedo: () => void;
  handleDelete: () => void;
  handleExport: () => void;
  handleSave?: () => void;
  previewMode: boolean;
  setPreviewMode: (mode: boolean) => void;
  hasAnimations: boolean;
  canUndo: boolean;
  canRedo: boolean;
  canDelete: boolean;
  selectedFormat: string;
  setSelectedFormat: (format: string) => void;
  hideCreateThumbnail?: boolean;
}

interface ControlsProps {
  selectedId: SelectionType;
  texts: TextItem[];
  logoProps: LogoProps;
  backgroundColor: string;
  setBackgroundColor: (color: string) => void;
  updateTextProperty: <K extends keyof TextItem>(property: K, value: TextItem[K], textId?: string) => void;
  updateLogoAnimation: (animation: AnimationType) => void;
  setBgImage: (image: string | null) => void;
  logoInputRef: React.RefObject<HTMLInputElement | null>;
  setLogoImage: (image: string | null) => void;
  setSelectedId: (id: SelectionType) => void;
  previewMode: boolean;
  bgImage: string | null;
}

type StageRefs = {
  stageRef: React.RefObject<Konva.Stage>;
  textRefs: React.RefObject<Map<string, Konva.Text>>;
  logoRef: React.RefObject<Konva.Image>;
  trRef: React.RefObject<Konva.Transformer>;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  logoInputRef: React.RefObject<HTMLInputElement | null>;
  inputRef: React.RefObject<HTMLTextAreaElement>;
};

interface StageProps {
  refs: StageRefs;
  bgImage: string | null;
  logoImage: string | null;
  backgroundColor: string;
  texts: TextItem[];
  setTexts: (updater: TextItem[] | ((prev: TextItem[]) => TextItem[])) => void;
  logoProps: LogoProps;
  setLogoProps: (updater: LogoProps | ((prev: LogoProps) => LogoProps)) => void;
  selectedId: SelectionType;
  setSelectedId: (id: SelectionType) => void;
  isEditingTextId: string | null;
  handleTextTransform: (id: string) => void;
  handleLogoTransform: () => void;
  handleLogoDoubleClick: () => void;
  previewMode: boolean;
  handleTextDoubleClick: (id: string) => void;
  [key: string]: unknown;
}

interface TextEditorProps {
  isEditingTextId: string | null;
  editingStyle: EditingStyle;
  texts: TextItem[];
  setTexts: (updater: TextItem[] | ((prev: TextItem[]) => TextItem[])) => void;
  setIsEditingTextId: (id: string | null) => void;
  setSelectedId: (id: SelectionType) => void;
  inputRef: React.RefObject<HTMLTextAreaElement>;
}

const MemoizedSidebar = memo(CanvasSidebar) as React.FC<SidebarProps>;
const MemoizedControls = memo(CanvasControls) as React.FC<ControlsProps>;
const MemoizedTextEditor = memo(TextEditor) as React.FC<TextEditorProps>;
const TypedCanvasStage = CanvasStage as unknown as React.FC<StageProps>;

const CanvasEditor: React.FC<CanvasEditorProps> = ({ onSave, hideCreateThumbnail }) => {
  const [selectedFormat, setSelectedFormat] = useState('png');
  const isOperationInProgress = useRef(false);
  const isMounted = useRef(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const keyHandlerRef = useRef<((e: KeyboardEvent) => void) | undefined>(undefined);

  const {
    stageRef,
    logoRef,
    trRef,
    textRefs,
    inputRef,
    fileInputRef,
    logoInputRef,
    setBgImage,
    setLogoImage,
    setSelectedId,
    setPreviewMode,
    setIsEditingTextId,
    bgImage,
    logoImage,
    texts,
    setTexts,
    backgroundColor,
    setBackgroundColor,
    logoProps,
    setLogoProps,
    selectedId,
    isEditingTextId,
    editingStyle,
    previewMode,
    canvasImages,
  } = useCanvasStore(
    useShallow((state) => ({
      stageRef: state.stageRef,
      logoRef: state.logoRef,
      trRef: state.trRef,
      textRefs: state.textRefs,
      inputRef: state.inputRef,
      fileInputRef: state.fileInputRef,
      logoInputRef: state.logoInputRef,
      setBgImage: state.setBgImage,
      setLogoImage: state.setLogoImage,
      setSelectedId: state.setSelectedId,
      setPreviewMode: state.setPreviewMode,
      setIsEditingTextId: state.setIsEditingTextId,
      bgImage: state.bgImage,
      logoImage: state.logoImage,
      texts: state.texts,
      setTexts: state.setTexts,
      backgroundColor: state.backgroundColor,
      setBackgroundColor: state.setBackgroundColor,
      logoProps: state.logoProps,
      setLogoProps: state.setLogoProps,
      selectedId: state.selectedId,
      isEditingTextId: state.isEditingTextId,
      editingStyle: state.editingStyle,
      previewMode: state.previewMode,
      canvasImages: state.canvasImages,
    }))
  );

  const { saveHistory } = useCanvasHistory();
  const { addText, updateTextProperty, updateLogoAnimation } = useCanvasElements();
  const { createAnimation } = useAnimation();
  const { handleTextTransform, handleLogoTransform } = useTransformHandlers();
  const { handleTextDoubleClick } = useTextEditing();
  const { handleBgUpload, handleLogoUpload } = useFileHandling();

  const history = useCanvasStore((s) => s.history);
  const future = useCanvasStore((s) => s.future);

  const hasAnimations = useMemo(() => {
    const textAnimations = texts.some((t) => t.animation && t.animation !== 'none');
    const logoAnimation = logoProps.animation && logoProps.animation !== 'none';
    const imageAnimations = canvasImages.some((img) => img.animation && img.animation !== 'none' && !img.deleted);
    return Boolean(textAnimations || logoAnimation || imageAnimations);
  }, [texts, logoProps.animation, canvasImages]);

  const handleLogoDoubleClick = useCallback(() => {
    setSelectedId('logo');
    if (logoRef.current && trRef.current) {
      const transformer = trRef.current as Konva.Transformer;
      transformer.enabledAnchors(['top-left', 'top-right', 'bottom-left', 'bottom-right']);
      transformer.nodes([logoRef.current as Konva.Node]);
      transformer.getLayer()?.batchDraw();
    }
  }, [logoRef, trRef, setSelectedId]);

  const handleAddText = useCallback(() => {
    saveHistory();
    const newId = addText();
    setSelectedId(newId);
  }, [saveHistory, addText, setSelectedId]);

  const handleDelete = useCallback(() => {
    if (!selectedId || selectedId === 'canvas' || isOperationInProgress.current) return;

    isOperationInProgress.current = true;
    try {
      if (isEditingTextId) setIsEditingTextId(null);

      if (selectedId === 'logo') {
        setLogoImage(null);
        setSelectedId(null);
      } else {
        saveHistory();
        const idToDelete = selectedId;
        setSelectedId(null);
        setTimeout(() => {
          setTexts((prev) => prev.filter((t) => t.id !== idToDelete));
        }, 0);
      }
    } finally {
      isOperationInProgress.current = false;
    }
  }, [selectedId, isEditingTextId, setIsEditingTextId, setSelectedId, setLogoImage, setTexts, saveHistory]);

  const handleExport = useCallback(() => {
    if (stageRef.current) {
      exportCanvasImage(stageRef.current, selectedFormat);
    }
  }, [stageRef, selectedFormat]);

  const handleUndo = useCallback(() => useCanvasStore.getState().undoChange(), []);
  const handleRedo = useCallback(() => useCanvasStore.getState().redoChange(), []);

  useEffect(() => {
    const currentContainer = containerRef.current;
    if (!currentContainer) return;

    keyHandlerRef.current = (e: KeyboardEvent): void => {
      if (!isMounted.current || !currentContainer.contains(e.target as Node)) return;

      if (
        (e.key === 'Delete' || e.key === 'Backspace') &&
        selectedId &&
        selectedId !== 'canvas' &&
        !isEditingTextId &&
        document.activeElement === document.body
      ) {
        e.preventDefault();
        e.stopPropagation();
        handleDelete();
      }
    };

    const keyHandler = keyHandlerRef.current;
    currentContainer.addEventListener('keydown', keyHandler, { passive: false, capture: true });
    currentContainer.setAttribute('aria-roledescription', 'canvas editor');

    return () => {
      if (currentContainer && keyHandler) {
        currentContainer.removeEventListener('keydown', keyHandler, { capture: true });
      }
      isMounted.current = false;
      keyHandlerRef.current = undefined;
    };
  }, [selectedId, isEditingTextId, handleDelete]);

  useEffect(() => {
    const animationIds: number[] = [];
    // Track animation delay timeouts so we can clear them on cleanup
    const delayTimeouts: number[] = [];
    const initialTextStates = new Map<
      string,
      { x: number; y: number; opacity: number; scale: { x: number; y: number }; rotation: number; color: string }
    >();
    const initialImageStates = new Map<
      string,
      { x: number; y: number; opacity: number; scale: { x: number; y: number }; rotation: number }
    >();
    let initialLogoState: {
      x: number;
      y: number;
      opacity: number;
      scale: { x: number; y: number };
      rotation: number;
    } | null = null;

    let loopTimerId: number | null = null;

    if (previewMode) {
      const animationDuration = useCanvasStore.getState().gifAnimationDuration * 1000;

      const setupAnimations = () => {
        animationIds.forEach((id) => {
          Konva.Animation.animations.forEach((a) => {
            if (a.id === id) a.stop();
          });
        });
        animationIds.length = 0;

        if (logoImage && logoRef.current && logoProps.animation && logoProps.animation !== 'none') {
          if (initialLogoState === null) {
            const konvaLogo = logoRef.current as unknown as Konva.Image;
            initialLogoState = {
              x: konvaLogo.x(),
              y: konvaLogo.y(),
              opacity: konvaLogo.opacity(),
              scale: { x: konvaLogo.scaleX(), y: konvaLogo.scaleY() },
              rotation: konvaLogo.rotation(),
            };
          }

          // Reset to initial state
          logoRef.current.scale({ x: 1, y: 1 });
          logoRef.current.rotation(0);
          logoRef.current.opacity(1);
          logoRef.current.visible(true);

          const anim = createAnimation(logoRef.current, logoProps.animation, logoProps);
          if (anim) animationIds.push(anim.id);
        }

        texts.forEach(({ id, animation, color, animationDelay, animationDuration }) => {
          if (!animation || animation === 'none') return;

          const node = textRefs.current.get(id);
          if (!node) return;

          const konvaNode = node as Konva.Text;
          konvaNode.scale({ x: 1, y: 1 });
          konvaNode.rotation(0);
          konvaNode.opacity(1);
          konvaNode.visible(true);

          const anim = createAnimation(konvaNode, animation, {
            color,
            duration: (animationDuration || 1) * 1000,
          });
          if (anim) animationIds.push(anim.id);
        });

        canvasImages.forEach((img) => {
          if (!img.animation || img.animation === 'none' || img.deleted) return;

          const imageNode = stageRef.current?.findOne(`#${img.id}`);
          if (!imageNode) return;

          const konvaNode = imageNode as Konva.Image;
          konvaNode.scale({ x: 1, y: 1 });
          konvaNode.rotation(img.rotation || 0);
          konvaNode.opacity(1);
          konvaNode.visible(true);

          const anim = createAnimation(konvaNode, img.animation as AnimationType, {
            duration: (img.animationDuration || 1) * 1000,
          });
          if (anim) animationIds.push(anim.id);
        });
      };

      setupAnimations();

      loopTimerId = window.setInterval(() => {
        if (stageRef.current) {
          setupAnimations();
        }
      }, animationDuration);

      texts.forEach(({ id, animation, color, animationDelay, animationDuration }) => {
        if (!animation || animation === 'none') return;

        const node = textRefs.current.get(id);
        if (!node) return;

        const konvaNode = node as Konva.Text;
        initialTextStates.set(id, {
          x: konvaNode.x(),
          y: konvaNode.y(),
          opacity: konvaNode.opacity(),
          scale: { x: konvaNode.scaleX(), y: konvaNode.scaleY() },
          rotation: konvaNode.rotation(),
          color: String(konvaNode.fill()),
        });

        konvaNode.scale({ x: 1, y: 1 });
        konvaNode.rotation(0);

        const delay = Math.min(5000, Math.max(0, Number(animationDelay || 0) * 1000));
        if (delay > 0) {
          konvaNode.opacity(0);
          // Keep the node visible (transparent) so it doesn't disappear when animation preview is exited early
          konvaNode.visible(true);

          const timeoutId = window.setTimeout(() => {
            if (konvaNode) {
              konvaNode.visible(true);
              konvaNode.opacity(1);
              const anim = createAnimation(konvaNode, animation, {
                color,
                duration: (animationDuration || 1) * 1000,
              });
              if (anim) animationIds.push(anim.id);
            }
          }, delay);

          delayTimeouts.push(timeoutId);
        } else {
          const anim = createAnimation(konvaNode, animation, {
            color,
            duration: (animationDuration || 1) * 1000,
          });
          if (anim) animationIds.push(anim.id);
        }
      });

      // Fix image animations with the same approach
      canvasImages.forEach((img) => {
        if (!img.animation || img.animation === 'none' || img.deleted) return;

        const imageNode = stageRef.current?.findOne(`#${img.id}`);
        if (!imageNode) return;

        const konvaNode = imageNode as Konva.Image;
        initialImageStates.set(img.id, {
          x: konvaNode.x(),
          y: konvaNode.y(),
          opacity: konvaNode.opacity(),
          scale: { x: konvaNode.scaleX(), y: konvaNode.scaleY() },
          rotation: konvaNode.rotation(),
        });

        konvaNode.scale({ x: 1, y: 1 });
        konvaNode.rotation(img.rotation || 0);

        const delay = Math.min(5000, Math.max(0, Number(img.animationDelay || 0) * 1000));
        if (delay > 0) {
          konvaNode.opacity(0);
          // Keep visible but transparent
          konvaNode.visible(true);

          const timeoutId = window.setTimeout(() => {
            if (konvaNode) {
              konvaNode.visible(true);
              konvaNode.opacity(1);
              const anim = createAnimation(konvaNode, img.animation as AnimationType, {
                duration: (img.animationDuration || 1) * 1000,
              });
              if (anim) animationIds.push(anim.id);
            }
          }, delay);

          delayTimeouts.push(timeoutId);
        } else {
          const anim = createAnimation(konvaNode, img.animation as AnimationType, {
            duration: (img.animationDuration || 1) * 1000,
          });
          if (anim) animationIds.push(anim.id);
        }
      });
    }

    return () => {
      // Stop all animations
      animationIds.forEach((id) => {
        Konva.Animation.animations.forEach((a) => {
          if (a.id === id) a.stop();
        });
      });

      // Clear all pending animation delays to prevent them from firing after preview mode is exited
      delayTimeouts.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });

      // Clear the loop timer
      if (loopTimerId !== null) {
        window.clearInterval(loopTimerId);
      }

      // Reset all elements to their original state
      initialTextStates.forEach((state, id) => {
        const node = textRefs.current.get(id);
        if (node) {
          const konvaNode = node as Konva.Text;
          konvaNode.x(state.x);
          konvaNode.y(state.y);
          konvaNode.opacity(state.opacity);
          konvaNode.scaleX(state.scale.x);
          konvaNode.scaleY(state.scale.y);
          konvaNode.rotation(state.rotation);
          konvaNode.fill(state.color);
          konvaNode.visible(true);
          konvaNode.getLayer()?.batchDraw();
        }
      });

      initialImageStates.forEach((state, id) => {
        const imageNode = stageRef.current?.findOne(`#${id}`);
        if (imageNode) {
          const konvaNode = imageNode as Konva.Image;
          konvaNode.x(state.x);
          konvaNode.y(state.y);
          konvaNode.opacity(state.opacity);
          konvaNode.scaleX(state.scale.x);
          konvaNode.scaleY(state.scale.y);
          konvaNode.rotation(state.rotation);
          konvaNode.visible(true);
          konvaNode.getLayer()?.batchDraw();
        }
      });

      if (initialLogoState && logoRef.current) {
        const konvaLogo = logoRef.current as unknown as Konva.Image;
        konvaLogo.x(initialLogoState.x);
        konvaLogo.y(initialLogoState.y);
        konvaLogo.opacity(initialLogoState.opacity);
        konvaLogo.scaleX(initialLogoState.scale.x);
        konvaLogo.scaleY(initialLogoState.scale.y);
        konvaLogo.rotation(initialLogoState.rotation);
        konvaLogo.visible(true);
        konvaLogo.getLayer()?.batchDraw();
      }

      if (stageRef.current) {
        stageRef.current.getLayers().forEach((layer) => layer.batchDraw());
      }
    };
  }, [previewMode, texts, canvasImages, logoImage, logoProps.animation, createAnimation, logoRef, textRefs, stageRef]);

  const updateTransformerForMultiSelection = useCallback(() => {
    if (!trRef.current || !stageRef.current || !Array.isArray(selectedId)) return;

    const transformer = trRef.current as unknown as Konva.Transformer;
    const nodes: Konva.Node[] = [];

    const allNodes = stageRef.current.find('Text, Image');
    allNodes.forEach((node) => {
      if (node.id() !== 'logo' && !node.id().includes('background')) {
        node.setAttr('stroke', undefined);
        node.setAttr('strokeWidth', 0);
        node.setAttr('className', '');
      }
    });

    selectedId.forEach((id) => {
      const textNode = stageRef.current?.findOne(`#text-${id}`);
      if (textNode) {
        nodes.push(textNode as Konva.Node);
        textNode.setAttr('stroke', '#3498db');
        textNode.setAttr('strokeWidth', 1);
        textNode.setAttr('className', 'multi-selected');
      }

      const imgNode = stageRef.current?.findOne(`#${id}`);
      if (imgNode) {
        nodes.push(imgNode as Konva.Node);
        imgNode.setAttr('stroke', '#3498db');
        imgNode.setAttr('strokeWidth', 1);
        imgNode.setAttr('className', 'multi-selected');
      }
    });

    if (nodes.length > 0) {
      transformer.nodes(nodes);
      transformer.getLayer()?.batchDraw();
    }
  }, [selectedId, stageRef, trRef]);

  const handlePreviewToggle = useCallback(
    (newPreviewMode: boolean) => {
      setPreviewMode(newPreviewMode);

      if (!newPreviewMode && stageRef.current) {
        setTimeout(() => {
          if (selectedId && trRef.current && stageRef.current) {
            if (Array.isArray(selectedId) && selectedId.length > 1) {
              updateTransformerForMultiSelection();
            } else if (selectedId === 'logo' && logoRef.current) {
              trRef.current.nodes([logoRef.current as unknown as Konva.Node]);
            } else if (selectedId !== 'canvas') {
              const node =
                stageRef.current.findOne(`#${selectedId}`) || stageRef.current.findOne(`#text-${selectedId}`);
              if (node) {
                trRef.current.nodes([node as Konva.Node]);
              }
            }

            trRef.current.getLayer()?.batchDraw();
          }

          stageRef.current.getLayers().forEach((layer) => layer.batchDraw());
        }, 50);
      }
    },
    [setPreviewMode, stageRef, trRef, selectedId, logoRef, updateTransformerForMultiSelection]
  );

  useKeyboardEventHandler();

  return (
    <article
      className="canvas-editor-container"
      ref={containerRef}
      tabIndex={-1}
      data-testid="canvas-editor"
      role="application"
    >
      <MemoizedSidebar
        fileInputRef={fileInputRef}
        logoInputRef={logoInputRef}
        handleAddText={handleAddText}
        handleUndo={handleUndo}
        handleRedo={handleRedo}
        handleDelete={handleDelete}
        handleExport={handleExport}
        handleSave={onSave}
        previewMode={previewMode}
        setPreviewMode={setPreviewMode}
        hasAnimations={hasAnimations}
        canUndo={history.length > 0}
        canRedo={future.length > 0}
        canDelete={!!selectedId && selectedId !== 'canvas'}
        selectedFormat={selectedFormat}
        setSelectedFormat={setSelectedFormat}
        hideCreateThumbnail={hideCreateThumbnail}
      />

      <main className="canvas-container">
        <figure className="canvas-wrapper zngly-canvas-wrapper">
          <TypedCanvasStage
            refs={{
              stageRef,
              textRefs,
              logoRef,
              trRef,
              fileInputRef,
              logoInputRef,
              inputRef,
            }}
            bgImage={bgImage}
            logoImage={logoImage}
            backgroundColor={backgroundColor}
            texts={texts}
            setTexts={setTexts}
            logoProps={logoProps}
            setLogoProps={setLogoProps}
            selectedId={selectedId}
            setSelectedId={setSelectedId}
            isEditingTextId={isEditingTextId}
            handleTextTransform={handleTextTransform}
            handleLogoTransform={handleLogoTransform}
            handleLogoDoubleClick={handleLogoDoubleClick}
            previewMode={previewMode}
            handleTextDoubleClick={handleTextDoubleClick}
          />
        </figure>

        <section className="canvas-help-text">
          <p>
            Click canvas to edit background. Click text/logo to select and resize. Double-click text to edit content.
          </p>

          {previewMode && <p>Preview mode: Animations are running. Click "Exit Preview" to return to edit mode.</p>}

          {hasAnimations && !previewMode && (
            <p className="animation-hint">Animations detected. Click "Animation Preview" to see them in action.</p>
          )}
        </section>
      </main>

      <div className="canvas-controls-panel">
        <MemoizedControls
          selectedId={selectedId}
          texts={texts}
          logoProps={logoProps}
          backgroundColor={backgroundColor}
          setBackgroundColor={setBackgroundColor}
          updateTextProperty={updateTextProperty}
          updateLogoAnimation={updateLogoAnimation}
          setBgImage={setBgImage}
          logoInputRef={logoInputRef}
          setLogoImage={setLogoImage}
          setSelectedId={setSelectedId}
          previewMode={previewMode}
          bgImage={bgImage}
        />
      </div>

      <MemoizedTextEditor
        isEditingTextId={isEditingTextId}
        editingStyle={editingStyle}
        texts={texts}
        setTexts={setTexts}
        setIsEditingTextId={setIsEditingTextId}
        setSelectedId={setSelectedId}
        inputRef={inputRef}
      />

      <input
        type="file"
        accept="image/*"
        ref={(el) => {
          if (el) fileInputRef.current = el;
        }}
        onChange={handleBgUpload}
        style={{ display: 'none' }}
      />
      <input
        type="file"
        accept="image/*"
        multiple
        ref={(el) => {
          if (el) logoInputRef.current = el;
        }}
        onChange={handleLogoUpload}
        style={{ display: 'none' }}
      />
    </article>
  );
};

export default memo(CanvasEditor);
